import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, pollFinalizeJob } from "@/lib/api";
import { BookingsCalendar } from "@/components/admin/BookingsCalendar";
import { ContactChatDialog } from "@/components/admin/ContactChat";
import { SessionPackagesCard, CreditAccountsCard, EventsCard, QrCodesCard, CourseBroadcastCard } from "@/components/admin/GrowthCards";
import { DEFAULT_PRIVACY, DEFAULT_TERMS } from "@/pages/PrivacyPage";
import { CaretDown, CaretUp, Trash, Plus, UploadSimple, GoogleLogo, Envelope, CalendarCheck, ChatCircleText, EnvelopeSimple } from "@phosphor-icons/react";

const Card = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { if (defaultOpen) setOpen(true); }, [defaultOpen]);
  return (
    <div className="rounded-xl border border-[#C4C8CE] bg-white mb-4">
      <button className="w-full flex items-center justify-between px-4 py-3 bg-transparent border-0 cursor-pointer"
        onClick={() => setOpen(!open)} data-testid={`website-card-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
        <span className="font-display font-bold text-[#000000]">{title}</span>
        {open ? <CaretUp size={16} /> : <CaretDown size={16} />}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
};

const SubSection = ({ title, children, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#EDEEF0] rounded-lg mb-2 bg-[#FAFAFA]">
      <button type="button" className="w-full flex items-center justify-between px-3 py-2.5 bg-transparent border-0 cursor-pointer"
        onClick={() => setOpen(!open)} data-testid={testid}>
        <span className="text-sm font-bold text-[#000000]">{title}</span>
        {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
      </button>
      {open && <div className="px-3 pb-3 bg-white rounded-b-lg pt-2">{children}</div>}
    </div>
  );
};

const Field = ({ label, value, onChange, textarea, placeholder, testid }) => (
  <label className="block text-xs font-semibold text-[#000000] mb-3">{label}
    {textarea ? (
      <textarea data-testid={testid} className="input-base mt-1 w-full text-sm" rows={3} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input data-testid={testid} className="input-base mt-1 w-full text-sm" value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    )}
  </label>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 text-sm font-semibold text-[#000000] mb-3 cursor-pointer">
    <input type="checkbox" data-testid={testid} checked={!!checked} onChange={(e) => onChange(e.target.checked)} /> {label}
  </label>
);

const PdfField = ({ label, value, onChange, testid }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("name", file.name);
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      onChange(data.url);
      toast.success("PDF uploaded");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mb-3">
      <div className="text-xs font-semibold text-[#000000] mb-1">{label}</div>
      <div className="flex items-center gap-2">
        {value ? (
          <a href={value} target="_blank" rel="noreferrer" className="text-xs text-[#1D4ED8] underline truncate max-w-[180px]">Attached PDF ↗</a>
        ) : (
          <span className="text-xs text-[#9CA3AF]">No PDF — email sends without attachment</span>
        )}
        <label className="btn-secondary text-xs cursor-pointer flex items-center gap-1 whitespace-nowrap">
          <UploadSimple size={14} /> {busy ? "…" : value ? "Replace" : "Upload PDF"}
          <input type="file" accept="application/pdf" className="hidden" data-testid={testid} onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        {value && <button className="btn-secondary text-xs" onClick={() => onChange("")}>Remove</button>}
      </div>
    </div>
  );
};

const ImageField = ({ label, value, onChange, testid }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mb-3">
      <div className="text-xs font-semibold text-[#000000] mb-1">{label}</div>
      <div className="flex items-center gap-2">
        {value && <img src={value} alt="" className="w-14 h-10 object-cover rounded border border-[#C4C8CE]" />}
        <input data-testid={testid} className="input-base flex-1 text-xs" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL" />
        <label className="btn-secondary text-xs cursor-pointer flex items-center gap-1 whitespace-nowrap">
          <UploadSimple size={14} /> {busy ? "…" : "Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
      </div>
    </div>
  );
};

const uploadVideoChunked = async (file, onProgress) => {
  const { sniffVideoContainer, CORRUPT_FILE_HELP } = await import("@/lib/videoCheck");
  if (!(await sniffVideoContainer(file))) throw new Error(CORRUPT_FILE_HELP);
  const CHUNK = 8 * 1024 * 1024;
  const uploadId = crypto.randomUUID();
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("chunk_index", i);
    fd.append("file", file.slice(i * CHUNK, Math.min(file.size, (i + 1) * CHUNK)), file.name);
    let attempt = 0;
    for (;;) {
      try {
        await api.post("/admin/uploads/chunk", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
        break;
      } catch (e) {
        attempt += 1;
        if (attempt >= 4) throw e;
        await new Promise((r) => setTimeout(r, 1500 * attempt));
      }
    }
    onProgress(Math.min(99, Math.round(((i + 1) / total) * 100)));
  }
  const { data } = await api.post("/admin/uploads/finalize-video", { upload_id: uploadId, filename: file.name, label: "", aspect_ratio: "16:9" }, { timeout: 300000 });
  const video = await pollFinalizeJob(data.job_id);
  onProgress(100);
  return video.url;
};

const VideoSlot = ({ label, value, onChange }) => {
  const [pct, setPct] = useState(null);
  const upload = async (file) => {
    if (!file) return;
    setPct(0);
    try {
      const url = await uploadVideoChunked(file, setPct);
      onChange({ ...(value || {}), url }, true);
      toast.success("Video uploaded and saved — it's live");
    } catch (e) { toast.error(formatApiError(e)); } finally { setPct(null); }
  };
  return (
    <div className="border border-[#EDEEF0] rounded-lg p-3 mb-2">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-bold text-[#000000]">{label}</div>
        {value?.url && (
          <button type="button" className="btn-ghost p-1 cursor-pointer flex items-center gap-1 text-xs text-[#991B1B]"
            onClick={() => onChange({ url: "", title: "" }, true)} data-testid={`remove-${label.toLowerCase().replace(" ", "-")}`}>
            <Trash size={14} /> Remove
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 mb-2">
        <input className="input-base flex-1 text-xs" placeholder="Video URL (or upload below)" value={value?.url || ""} onChange={(e) => onChange({ ...(value || {}), url: e.target.value })} />
        <label className="btn-secondary text-xs cursor-pointer flex items-center gap-1 whitespace-nowrap">
          <UploadSimple size={14} /> {pct !== null ? `${pct}%` : "Upload video"}
          <input type="file" accept="video/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
      </div>
      <input className="input-base w-full text-xs" placeholder="Caption under this video (optional)" value={value?.title || ""} onChange={(e) => onChange({ ...(value || {}), title: e.target.value })} />
      {value?.url && <video src={value.url} controls preload="metadata" className="mt-2 w-full max-w-xs rounded border border-[#EDEEF0]" />}
    </div>
  );
};

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const CommsLogCard = () => {
  const [rows, setRows] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/admin/comms-log", { params: { limit: 50 } }); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card title="Communications Log — Emails & Texts">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-[#000000]">Every outgoing email and text (invoices, bookings, reminders). Texts show as "skipped" until Twilio SMS is connected.</div>
        <button className="btn-secondary text-xs" onClick={load} data-testid="comms-log-refresh">{rows === null ? "Load log" : "Refresh"}</button>
      </div>
      {rows !== null && (rows.length === 0 ? (
        <div className="text-sm text-[#000000]" data-testid="comms-log-empty">No messages logged yet.</div>
      ) : rows.map((r) => (
        <div key={r.id} className="border-b border-[#EDEEF0] py-1.5 text-sm flex items-center gap-2 flex-wrap" data-testid="comms-log-row">
          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${r.status === "sent" ? "bg-[#DCFCE7] text-[#166534]" : r.status === "skipped" ? "bg-[#F4F4F5] text-[#000000]" : "bg-[#FEE2E2] text-[#B91C1C]"}`}>
            {r.status === "sent" ? "Sent ✓" : r.status === "skipped" ? "Skipped" : "Failed ✗"}
          </span>
          <span className="text-[10px] uppercase text-[#000000]">{r.channel}</span>
          {r.kind && <span className="text-[10px] uppercase text-[#1D4ED8]">{r.kind}</span>}
          <span className="text-xs text-[#000000] truncate max-w-[220px]" title={r.subject}>{r.subject}</span>
          <span className="text-xs text-[#000000] break-all">→ {r.to}</span>
          <span className="text-[11px] text-[#000000] ml-auto">{new Date(r.created_at).toLocaleString()}</span>
        </div>
      )))}
    </Card>
  );
};

const LoginAlertsCard = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const load = () => api.get("/admin/login-watch").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get("/admin/login-watch/search", { params: { q } }).then(({ data }) => setResults(data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const add = async (r) => {
    try {
      await api.post("/admin/login-watch", r);
      setQ(""); setResults([]); load();
      toast.success(`${r.name} added — you'll get an email when they sign in`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggle = async (it) => {
    try {
      const { data } = await api.put(`/admin/login-watch/${it.id}`, { enabled: !it.enabled });
      setItems((p) => p.map((x) => (x.id === it.id ? data : x)));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (it) => {
    try { await api.delete(`/admin/login-watch/${it.id}`); setItems((p) => p.filter((x) => x.id !== it.id)); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const watchedIds = new Set(items.map((i) => i.target_id));
  return (
    <Card title={`Login Alerts${items.filter((i) => i.enabled).length ? ` (${items.filter((i) => i.enabled).length} active)` : ""}`}>
      <div className="text-xs text-[#000000] mb-2">
        Temporarily track specific people — you get an email the moment they sign in. Toggle off (or remove) anytime.
      </div>
      <div className="relative mb-3">
        <input className="input-base w-full text-sm" placeholder="Search a user or student by name or email…"
          value={q} onChange={(e) => setQ(e.target.value)} data-testid="login-watch-search" />
        {results.length > 0 && (
          <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-[#C4C8CE] rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="login-watch-results">
            {results.map((r) => (
              <button key={r.target_id} type="button"
                className="w-full text-left px-3 py-2 text-sm bg-transparent border-0 cursor-pointer hover:bg-[#F4F4F5] disabled:opacity-40 flex items-center gap-2"
                disabled={watchedIds.has(r.target_id)} onClick={() => add(r)}
                data-testid={`login-watch-result-${r.target_id}`}>
                <Plus size={13} weight="bold" />
                <span className="font-semibold">{r.name}</span>
                <span className="text-xs text-[#000000]">{r.detail}</span>
                {watchedIds.has(r.target_id) && <span className="text-[10px] uppercase font-bold text-[#166534] ml-auto">On list</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      {items.length === 0 && <div className="text-sm text-[#000000]" data-testid="login-watch-empty">Nobody on the watch list.</div>}
      {items.map((it) => (
        <div key={it.id} className="flex items-center gap-2 border border-[#EDEEF0] rounded-lg p-2.5 mb-2" data-testid={`login-watch-row-${it.id}`}>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-[#000000]">{it.name}</div>
            <div className="text-xs text-[#000000] truncate">{it.detail}{it.target_type === "student" ? " · school student" : ""}</div>
          </div>
          <label className="flex items-center gap-1.5 text-xs font-semibold text-[#000000] cursor-pointer" title="Email me when this person signs in">
            <input type="checkbox" checked={!!it.enabled} onChange={() => toggle(it)} data-testid={`login-watch-toggle-${it.id}`} />
            Alerts
          </label>
          <button className="btn-ghost p-1 cursor-pointer text-[#991B1B]" onClick={() => remove(it)} title="Remove from watch list" data-testid={`login-watch-remove-${it.id}`}>
            <Trash size={15} />
          </button>
        </div>
      ))}
    </Card>
  );
};

const BlogCard = () => {
  const [posts, setPosts] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/blog").then(({ data }) => setPosts(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit.title.trim()) { toast.error("Give the post a title"); return; }
    setBusy(true);
    try {
      if (edit.id) await api.put(`/admin/blog/${edit.id}`, edit);
      else await api.post("/admin/blog", edit);
      toast.success(edit.published ? "Post saved & live on the blog" : "Draft saved");
      setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await api.delete(`/admin/blog/${p.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const togglePublish = async (p) => {
    try { await api.put(`/admin/blog/${p.id}`, { published: !p.published }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card title="Blog">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-xs text-[#000000] max-w-md">Articles appear at <b>/blog</b> on your marketing site (linked in the nav + footer). Blank line = paragraph, start a line with <b>##&nbsp;</b> for a heading, <b>-&nbsp;</b> for bullets.</p>
        <button className="btn-primary text-xs flex items-center gap-1" data-testid="blog-new-post"
          onClick={() => setEdit({ title: "", cover_url: "", excerpt: "", content: "", published: false })}>
          <Plus size={13} weight="bold" /> New post
        </button>
      </div>
      {posts.length === 0 && <div className="text-sm text-[#000000]">No posts yet — write your first article!</div>}
      {posts.map((p) => (
        <div key={p.id} className="flex flex-wrap items-center gap-2 border border-[#EDEEF0] rounded-lg p-3 mb-2" data-testid={`blog-row-${p.id}`}>
          {p.cover_url && <img src={p.cover_url} alt="" className="w-12 h-9 object-cover rounded" />}
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm truncate">{p.title}</div>
            <div className="text-[11px] text-[#000000]">/blog/{p.slug}</div>
          </div>
          <span className="chip text-[10px] font-bold uppercase"
            style={p.published ? { background: "#DCFCE7", color: "#166534", border: "1px solid #86EFAC" } : { background: "#FEF3C7", color: "#92400E", border: "1px solid #FBBF24" }}>
            {p.published ? "Live" : "Draft"}
          </span>
          <button className="btn-secondary text-xs" data-testid={`blog-publish-${p.id}`} onClick={() => togglePublish(p)}>{p.published ? "Unpublish" : "Publish"}</button>
          <button className="btn-secondary text-xs" data-testid={`blog-edit-${p.id}`} onClick={() => setEdit({ ...p })}>Edit</button>
          <button className="btn-ghost p-1.5 text-[#B91C1C]" onClick={() => del(p)} data-testid={`blog-delete-${p.id}`}><Trash size={15} /></button>
        </div>
      ))}
      {edit && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="blog-editor-modal" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-5 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="font-bold mb-3">{edit.id ? "Edit post" : "New blog post"}</div>
            <label className="text-xs font-semibold text-[#000000]">Title</label>
            <input data-testid="blog-title-input" className="input-base w-full mb-3" value={edit.title}
              onChange={(e) => setEdit({ ...edit, title: e.target.value })} placeholder="5 De-escalation Phrases That Actually Work" />
            <ImageField label="Cover image (optional)" value={edit.cover_url} onChange={(v) => setEdit({ ...edit, cover_url: v })} testid="blog-cover-input" />
            <label className="text-xs font-semibold text-[#000000]">Excerpt <span className="font-normal text-[#000000]">(1–2 sentences shown on the blog list + Google)</span></label>
            <textarea data-testid="blog-excerpt-input" className="input-base w-full mb-3" rows={2} value={edit.excerpt}
              onChange={(e) => setEdit({ ...edit, excerpt: e.target.value })} />
            <label className="text-xs font-semibold text-[#000000]">Article <span className="font-normal text-[#000000]">(blank line = paragraph · "## " = heading · "- " = bullet)</span></label>
            <textarea data-testid="blog-content-input" className="input-base w-full mb-3 text-sm leading-relaxed" style={{ minHeight: 320, resize: "vertical" }}
              value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })} />
            <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
              <input type="checkbox" data-testid="blog-published-checkbox" checked={edit.published} onChange={(e) => setEdit({ ...edit, published: e.target.checked })} />
              Published (visible on the public blog)
            </label>
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setEdit(null)}>Cancel</button>
              <button className="btn-primary" disabled={busy} onClick={save} data-testid="blog-save-btn">{busy ? "Saving…" : "Save post"}</button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default function WebsitePanel({ section = "site" }) {
  const isCal = section === "calendar";
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [booking, setBooking] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [gcal, setGcal] = useState(null);
  const [newBlock, setNewBlock] = useState({ date: "", end_date: "", start_time: "", end_time: "", label: "" });
  const [searchParams] = useSearchParams();
  const highlightBooking = searchParams.get("booking") || "";
  const deepLinkDate = searchParams.get("date") || "";
  const [bookingView, setBookingView] = useState(highlightBooking ? "calendar" : "list");
  const [focus, setFocus] = useState(null); // list row click → jump calendar to that week
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [streakNudges, setStreakNudges] = useState(true);
  const [communityOn, setCommunityOn] = useState(false);
  useEffect(() => {
    api.get("/admin/business-identity").then(({ data }) => { setSmsEnabled(data.sms_enabled !== false); setStreakNudges(data.streak_nudges_enabled !== false); setCommunityOn(data.community_enabled === true); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!highlightBooking || !bookings.length) return;
    const el = document.querySelector(`[data-testid="booking-row-${highlightBooking}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightBooking, bookings]);

  const load = () => {
    api.get("/site/content").then(({ data }) => {
      const legal = data.legal || {};
      setC({
        ...data,
        legal: {
          ...legal,
          privacy: (legal.privacy || "").trim() ? legal.privacy : DEFAULT_PRIVACY,
          terms: (legal.terms || "").trim() ? legal.terms : DEFAULT_TERMS,
        },
      });
    });
    api.get("/admin/site/booking/settings").then(({ data }) => setBooking(data));
    api.get("/admin/site/booking/blocks").then(({ data }) => setBlocks(data));
    api.get("/admin/site/bookings").then(({ data }) => setBookings(data));
    api.get("/admin/site/messages").then(({ data }) => setMessages(data));
    api.get("/admin/site/gcal/status").then(({ data }) => setGcal(data)).catch(() => setGcal(null));
  };
  useEffect(load, []);

  const cRef = useRef(c);
  cRef.current = c;

  const setVideoSlot = (idx) => async (v, persist = false) => {
    const p = cRef.current;
    const arr = [...(p.video.videos || [])];
    while (arr.length <= idx) arr.push({});
    arr[idx] = v;
    const next = { ...p, video: { ...p.video, videos: arr } };
    setC(next);
    if (persist) {
      try { await api.put("/admin/site/content", next); } catch (e) { toast.error(formatApiError(e)); }
    }
  };

  const set = (section, key, val) => setC((p) => ({ ...p, [section]: { ...p[section], [key]: val } }));
  const setArr = (section, key, idx, field, val) => setC((p) => {
    const arr = [...(p[section][key] || [])];
    arr[idx] = { ...arr[idx], [field]: val };
    return { ...p, [section]: { ...p[section], [key]: arr } };
  });
  const addArr = (section, key, blank) => setC((p) => ({ ...p, [section]: { ...p[section], [key]: [...(p[section][key] || []), blank] } }));
  const delArr = (section, key, idx) => setC((p) => ({ ...p, [section]: { ...p[section], [key]: p[section][key].filter((_, i) => i !== idx) } }));

  const saveContent = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site/content", c);
      toast.success("Website saved — changes are live");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const saveBooking = async () => {
    try {
      await api.put("/admin/site/booking/settings", booking);
      toast.success("Booking settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addBlock = async () => {
    if (!newBlock.date) return toast.error("Pick a date");
    try {
      await api.post("/admin/site/booking/blocks", {
        ...newBlock,
        end_date: newBlock.end_date || null,
        start_time: newBlock.start_time || null,
        end_time: newBlock.end_time || null,
      });
      setNewBlock({ date: "", end_date: "", start_time: "", end_time: "", label: "" });
      api.get("/admin/site/booking/blocks").then(({ data }) => setBlocks(data));
      toast.success("Time blocked off");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delBlock = async (id) => {
    await api.delete(`/admin/site/booking/blocks/${id}`);
    setBlocks((p) => p.filter((b) => b.id !== id));
  };

  const actBooking = async (id, status) => {
    try {
      await api.patch(`/admin/site/bookings/${id}`, { status });
      api.get("/admin/site/bookings").then(({ data }) => setBookings(data));
      toast.success(status === "confirmed" ? "Booking confirmed" : "Booking updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const connectGcal = async () => {
    try {
      const { data } = await api.get("/admin/site/gcal/connect");
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {  // one-tap reconnect from the disconnect alert text
    if (searchParams.get("gcal") === "reconnect") connectGcal();
    if (searchParams.get("gcal") === "connected") toast.success("Google Calendar connected — bookings will sync from now on.");
    if (searchParams.get("gcal") === "error") toast.error(
      "Google Calendar connection failed. Most common cause: this site's address isn't registered as a redirect URI in Google Cloud Console → Credentials → your OAuth client → Authorized redirect URIs. It must contain exactly: " +
      `${window.location.origin}/api/site/gcal/callback`, { duration: 20000 });
    // eslint-disable-next-line
  }, []);

  const [chat, setChat] = useState(null);
  const [blockDlg, setBlockDlg] = useState(false);
  const [calRefresh, setCalRefresh] = useState(0);
  const syncGcalNow = async () => {
    try {
      const { data } = await api.post("/admin/site/gcal/reconcile");
      toast.success(data.connected
        ? `Synced — ${data.pushed} booking${data.pushed === 1 ? "" : "s"} pushed to Google Calendar`
        : "Google connection is broken — reconnect below");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setHours = (day, idx, pos, val) => setBooking((p) => {
    const hours = { ...(p.hours || {}) };
    const ranges = [...(hours[String(day)] || [])];
    ranges[idx] = [...ranges[idx]];
    ranges[idx][pos] = val;
    hours[String(day)] = ranges;
    return { ...p, hours };
  });
  const toggleDay = (day, on) => setBooking((p) => ({ ...p, hours: { ...p.hours, [String(day)]: on ? [["17:00", "21:00"]] : [] } }));

  if (!c || !booking) return <div className="text-[#000000]">Loading…</div>;
  const pendingCount = bookings.filter((b) => b.status === "pending").length;

  return (
    <div data-testid="website-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display font-bold text-xl text-[#000000]">{isCal ? "Calendar" : "Website"}</h2>
          <p className="text-xs text-[#000000]">{isCal
            ? "Your whole schedule in one place — requests, time off, settings and Google sync all drive off the visual calendar."
            : "Your public landing page at the root of your domain. Every change here is live the moment you save."}</p>
        </div>
        <div className="flex gap-2">
          {!isCal && <a className="btn-secondary text-sm no-underline" href="/" target="_blank" rel="noreferrer" data-testid="website-preview-link">View site</a>}
          <button className="btn-primary text-sm" onClick={saveContent} disabled={saving} data-testid="website-save-button">{saving ? "Saving…" : isCal ? "Save settings" : "Save website"}</button>
        </div>
      </div>

      {!isCal && <>
      <Card title="SEO (Search Engines)" defaultOpen>
        <Field label="Page title (shows in Google + browser tab)" value={c.seo.title} onChange={(v) => set("seo", "title", v)} testid="seo-title" />
        <Field label="Description (the grey text under your Google result — keep under 160 characters)" textarea value={c.seo.description} onChange={(v) => set("seo", "description", v)} testid="seo-description" />
      </Card>

      <Card title="Hero (Top Section)">
        <Toggle label="Show this section" checked={c.hero.enabled !== false} onChange={(v) => set("hero", "enabled", v)} testid="hero-enabled" />
        <Field label="Small label above the title" value={c.hero.kicker} onChange={(v) => set("hero", "kicker", v)} />
        <Field label="Big headline" value={c.hero.title} onChange={(v) => set("hero", "title", v)} testid="hero-title-input" />
        <Field label="Paragraph" textarea value={c.hero.body} onChange={(v) => set("hero", "body", v)} />
        <ImageField label="Logo (top-left of the site)" value={c.hero.logo_url} onChange={(v) => set("hero", "logo_url", v)} testid="hero-logo" />
        <ImageField label="Background photo" value={c.hero.image_url} onChange={(v) => set("hero", "image_url", v)} testid="hero-image" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Yellow button text" value={c.hero.cta_primary_label} onChange={(v) => set("hero", "cta_primary_label", v)} />
          <Field label="Outline button text" value={c.hero.cta_secondary_label} onChange={(v) => set("hero", "cta_secondary_label", v)} />
        </div>
        <Field label="Caption under the buttons (mentions in-person + virtual)" value={c.hero.cta_caption} onChange={(v) => set("hero", "cta_caption", v)} testid="hero-cta-caption-input" />
        <Toggle label="Show members badge" checked={c.hero.members_badge_enabled} onChange={(v) => set("hero", "members_badge_enabled", v)} testid="members-badge-toggle" />
        <Field label="Members badge text" value={c.hero.members_text} onChange={(v) => set("hero", "members_text", v)} testid="members-text" />
      </Card>

      <Card title="Moving Ticker Strip">
        <Toggle label="Show the yellow scrolling strip" checked={c.ticker?.enabled !== false} onChange={(v) => set("ticker", "enabled", v)} testid="ticker-enabled" />
        <Field label="Words (one per line)" textarea value={(c.ticker?.items || []).join("\n")} onChange={(v) => set("ticker", "items", v.split("\n").filter(Boolean))} testid="ticker-items" />
      </Card>

      <Card title="The Self-Agency Project Section">
        <Toggle label="Show this section" checked={c.sap?.enabled !== false} onChange={(v) => set("sap", "enabled", v)} testid="sap-enabled" />
        <Field label="Small label" value={c.sap?.kicker} onChange={(v) => set("sap", "kicker", v)} />
        <Field label="Headline" value={c.sap?.title} onChange={(v) => set("sap", "title", v)} testid="sap-title" />
        <Field label="Text" textarea value={c.sap?.body} onChange={(v) => set("sap", "body", v)} />
        <Field label="Button text" value={c.sap?.cta_label} onChange={(v) => set("sap", "cta_label", v)} />
        <Field label="Small note under text" value={c.sap?.note} onChange={(v) => set("sap", "note", v)} />
        <Toggle label="Show gold hook bar inside the hero (visible without scrolling)" checked={c.sap?.hero_hook_enabled !== false} onChange={(v) => set("sap", "hero_hook_enabled", v)} testid="sap-hook-enabled" />
        <Field label="Hero hook text" value={c.sap?.hero_hook_text} onChange={(v) => set("sap", "hero_hook_text", v)} testid="sap-hook-text" />
        <Field label="Hero hook button word" value={c.sap?.hero_hook_cta} onChange={(v) => set("sap", "hero_hook_cta", v)} />
        <Toggle label="Show floating button until visitor scrolls to this section" checked={c.sap?.float_enabled !== false} onChange={(v) => set("sap", "float_enabled", v)} testid="sap-float-enabled" />
        <Field label="Floating button text" value={c.sap?.float_text} onChange={(v) => set("sap", "float_text", v)} testid="sap-float-text" />
      </Card>

      <Card title="Problem Section">
        <Toggle label="Show this section" checked={c.problem.enabled !== false} onChange={(v) => set("problem", "enabled", v)} />
        <Field label="Small label" value={c.problem.kicker} onChange={(v) => set("problem", "kicker", v)} />
        <Field label="Headline" value={c.problem.title} onChange={(v) => set("problem", "title", v)} />
        <Field label="Paragraphs (blank line = new paragraph)" textarea value={c.problem.body} onChange={(v) => set("problem", "body", v)} />
        <ImageField label="Photo" value={c.problem.image_url} onChange={(v) => set("problem", "image_url", v)} />
        <div className="text-xs text-[#000000] mb-2">The two buttons here mirror the hero buttons — edit their text in the Hero section above.</div>
      </Card>

      <Card title="Lineage Section (Robbie Cressman / Camp X)">
        <Toggle label="Show this section" checked={c.lineage?.enabled !== false} onChange={(v) => set("lineage", "enabled", v)} />
        <Field label="Small label" value={c.lineage?.kicker} onChange={(v) => set("lineage", "kicker", v)} />
        <Field label="Headline" value={c.lineage?.title} onChange={(v) => set("lineage", "title", v)} testid="lineage-title" />
        <Field label="Story paragraphs (one per line)" textarea value={c.lineage?.body} onChange={(v) => set("lineage", "body", v)} />
        <ImageField label="Heritage photo" value={c.lineage?.image_url} onChange={(v) => set("lineage", "image_url", v)} />
        <Field label="Photo caption" value={c.lineage?.image_caption} onChange={(v) => set("lineage", "image_caption", v)} />
        <div className="text-xs font-bold text-[#000000] mt-3 mb-1">Lineage timeline</div>
        {(c.lineage?.chain || []).map((s, i) => (
          <div key={i} className="border border-[#EDEEF0] rounded-lg p-3 mb-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-bold text-[#000000]">Step {i + 1}</span>
              <button className="btn-ghost p-1 cursor-pointer" onClick={() => delArr("lineage", "chain", i)}><Trash size={14} /></button>
            </div>
            <Field label="Era" value={s.era} onChange={(v) => setArr("lineage", "chain", i, "era", v)} />
            <Field label="Name" value={s.name} onChange={(v) => setArr("lineage", "chain", i, "name", v)} />
            <Field label="Text" value={s.text} onChange={(v) => setArr("lineage", "chain", i, "text", v)} />
          </div>
        ))}
        <button className="btn-secondary text-xs mb-3" onClick={() => addArr("lineage", "chain", { era: "", name: "", text: "" })} data-testid="lineage-add-chain">+ Add timeline step</button>
        <Field label="Books heading" value={c.lineage?.books_heading} onChange={(v) => set("lineage", "books_heading", v)} />
        <Field label="Books intro text" textarea value={c.lineage?.books_blurb} onChange={(v) => set("lineage", "books_blurb", v)} />
        {(c.lineage?.books || []).map((b, i) => (
          <div key={i} className="border border-[#EDEEF0] rounded-lg p-3 mb-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-bold text-[#000000]">Book {i + 1}</span>
              <button className="btn-ghost p-1 cursor-pointer" onClick={() => delArr("lineage", "books", i)}><Trash size={14} /></button>
            </div>
            <Field label="Title" value={b.title} onChange={(v) => setArr("lineage", "books", i, "title", v)} />
            <Field label="One-line description" value={b.tagline} onChange={(v) => setArr("lineage", "books", i, "tagline", v)} />
            <Field label="Amazon link" value={b.url} onChange={(v) => setArr("lineage", "books", i, "url", v)} />
            <ImageField label="Cover image (optional — a styled title card shows if empty)" value={b.img} onChange={(v) => setArr("lineage", "books", i, "img", v)} />
          </div>
        ))}
        <button className="btn-secondary text-xs" onClick={() => addArr("lineage", "books", { title: "", tagline: "", url: "", img: "" })} data-testid="lineage-add-book">+ Add book</button>
        <Field label="'All books' button text" value={c.lineage?.books_cta} onChange={(v) => set("lineage", "books_cta", v)} />
        <Field label="'All books' button link" value={c.lineage?.books_cta_url} onChange={(v) => set("lineage", "books_cta_url", v)} />
      </Card>

      <Card title="The System (4 Pillars)">
        <Toggle label="Show this section" checked={c.system.enabled !== false} onChange={(v) => set("system", "enabled", v)} />
        <Field label="Headline" value={c.system.title} onChange={(v) => set("system", "title", v)} />
        {(c.system.pillars || []).map((p, i) => (
          <div key={i} className="border border-[#EDEEF0] rounded-lg p-3 mb-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-bold text-[#000000]">Pillar {i + 1}</span>
              <button className="btn-ghost p-1 cursor-pointer" onClick={() => delArr("system", "pillars", i)}><Trash size={14} /></button>
            </div>
            <Field label="Title" value={p.title} onChange={(v) => setArr("system", "pillars", i, "title", v)} />
            <Field label="Text" textarea value={p.body} onChange={(v) => setArr("system", "pillars", i, "body", v)} />
          </div>
        ))}
        <button className="btn-secondary text-xs flex items-center gap-1" onClick={() => addArr("system", "pillars", { title: "", body: "" })}><Plus size={13} /> Add pillar</button>
        <div className="mt-3"><Field label="Closing quote" value={c.system.outro} onChange={(v) => set("system", "outro", v)} /></div>
      </Card>

      <Card title="Video Section (module)">
        <Toggle label="Show video section" checked={c.video.enabled} onChange={(v) => set("video", "enabled", v)} testid="video-enabled" />
        <Field label="Headline" value={c.video.title} onChange={(v) => set("video", "title", v)} />
        <Field label="Text" textarea value={c.video.body} onChange={(v) => set("video", "body", v)} />
        <VideoSlot label="Video 1" value={(c.video.videos || [])[0]} onChange={setVideoSlot(0)} />
        <VideoSlot label="Video 2" value={(c.video.videos || [])[1]} onChange={setVideoSlot(1)} />
        <div className="text-xs text-[#000000]">Uploads and removals save themselves instantly. Captions and other text still need "Save website".</div>
      </Card>

      <Card title="Pricing">
        <Toggle label="Show this section" checked={c.pricing.enabled !== false} onChange={(v) => set("pricing", "enabled", v)} />
        <Field label="Headline" value={c.pricing.title} onChange={(v) => set("pricing", "title", v)} />
        <Field label="Subtitle" value={c.pricing.subtitle} onChange={(v) => set("pricing", "subtitle", v)} />
        {(c.pricing.cards || []).map((card, i) => (
          <div key={i} className="border border-[#EDEEF0] rounded-lg p-3 mb-2">
            <div className="text-xs font-bold text-[#000000] mb-1">Card {i + 1}</div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Name" value={card.name} onChange={(v) => setArr("pricing", "cards", i, "name", v)} />
              <Field label="Price" value={card.price} onChange={(v) => setArr("pricing", "cards", i, "price", v)} />
              <Field label="Price note" value={card.price_note} onChange={(v) => setArr("pricing", "cards", i, "price_note", v)} />
            </div>
            <Field label="Blurb" textarea value={card.blurb} onChange={(v) => setArr("pricing", "cards", i, "blurb", v)} />
            <Field label="Features (one per line)" textarea value={(card.features || []).join("\n")} onChange={(v) => setArr("pricing", "cards", i, "features", v.split("\n"))} />
            <div className="grid grid-cols-3 gap-3 items-end">
              <Field label="Button text" value={card.cta_label} onChange={(v) => setArr("pricing", "cards", i, "cta_label", v)} />
              <label className="block text-xs font-semibold text-[#000000] mb-3">Button action
                <select className="input-base mt-1 w-full text-sm" value={card.cta} onChange={(e) => setArr("pricing", "cards", i, "cta", e.target.value)}>
                  <option value="app">Open online course</option>
                  <option value="book">Open booking calendar</option>
                  <option value="phone">Call phone number</option>
                </select>
              </label>
              <Toggle label="Highlight (Most Popular)" checked={card.highlight} onChange={(v) => setArr("pricing", "cards", i, "highlight", v)} />
            </div>
          </div>
        ))}
      </Card>

      <Card title="FAQs">
        <Toggle label="Show this section" checked={c.faqs.enabled !== false} onChange={(v) => set("faqs", "enabled", v)} />
        {(c.faqs.items || []).map((f, i) => (
          <div key={i} className="border border-[#EDEEF0] rounded-lg p-3 mb-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-bold text-[#000000]">Q{i + 1}</span>
              <button className="btn-ghost p-1 cursor-pointer" onClick={() => delArr("faqs", "items", i)}><Trash size={14} /></button>
            </div>
            <Field label="Question" value={f.q} onChange={(v) => setArr("faqs", "items", i, "q", v)} />
            <Field label="Answer" textarea value={f.a} onChange={(v) => setArr("faqs", "items", i, "a", v)} />
          </div>
        ))}
        <button className="btn-secondary text-xs flex items-center gap-1" onClick={() => addArr("faqs", "items", { q: "", a: "" })}><Plus size={13} /> Add FAQ</button>
      </Card>

      <Card title="Testimonials">
        <Toggle label="Show this section" checked={c.testimonials.enabled !== false} onChange={(v) => set("testimonials", "enabled", v)} />
        {(c.testimonials.items || []).map((t, i) => (
          <div key={i} className="border border-[#EDEEF0] rounded-lg p-3 mb-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-bold text-[#000000]">#{i + 1}</span>
              <button className="btn-ghost p-1 cursor-pointer" onClick={() => delArr("testimonials", "items", i)}><Trash size={14} /></button>
            </div>
            <Field label="Quote" textarea value={t.quote} onChange={(v) => setArr("testimonials", "items", i, "quote", v)} />
            <Field label="Name" value={t.name} onChange={(v) => setArr("testimonials", "items", i, "name", v)} />
          </div>
        ))}
        <button className="btn-secondary text-xs flex items-center gap-1" onClick={() => addArr("testimonials", "items", { quote: "", name: "" })}><Plus size={13} /> Add testimonial</button>
      </Card>

      <Card title="Contact & Footer">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone (shown on site)" value={c.contact.phone} onChange={(v) => set("contact", "phone", v)} />
          <Field label="Phone (dialing format, e.g. +17802285325)" value={c.contact.phone_raw} onChange={(v) => set("contact", "phone_raw", v)} />
        </div>
        <Toggle label="Show contact form" checked={c.contact.form_enabled !== false} onChange={(v) => set("contact", "form_enabled", v)} />
        <Field label="Footer tagline" value={c.footer.tagline} onChange={(v) => set("footer", "tagline", v)} />
        <Field label="Footer app line" value={c.footer.app_line} onChange={(v) => set("footer", "app_line", v)} />
      </Card>
      </>}

      {isCal && <>
      <Card title={`Calendar${pendingCount ? ` (${pendingCount} pending)` : ""}`} defaultOpen>
        <div className="flex gap-2 mb-3 items-center flex-wrap">
          {["list", "calendar"].map((v) => (
            <button key={v} type="button" data-testid={`bookings-view-${v}`}
              className="text-xs font-bold uppercase px-3 py-1.5 rounded-full cursor-pointer"
              style={{
                background: bookingView === v ? "#0A0D14" : "#fff",
                color: bookingView === v ? "#fff" : "#52525B",
                border: "1px solid " + (bookingView === v ? "#0A0D14" : "#C4C8CE"),
              }}
              onClick={() => { setBookingView(v); if (v === "list") setFocus(null); }}>
              {v === "list" ? "List" : "Calendar"}
            </button>
          ))}
          <button type="button" className="btn-secondary text-xs" data-testid="cal-block-btn"
            onClick={() => { setNewBlock({ date: "", end_date: "", start_time: "", end_time: "", label: "" }); setBlockDlg(true); }}>
            + Block time off
          </button>
          {gcal && gcal.configured && (gcal.connected ? (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-[#3F6212]" data-testid="cal-gcal-chip">
              <GoogleLogo size={13} /> Synced
              <button type="button" className="bg-transparent border-0 cursor-pointer text-xs font-bold text-[#3F6212] underline underline-offset-2 p-0"
                onClick={syncGcalNow} data-testid="cal-gcal-sync">Sync now</button>
            </span>
          ) : (
            <button type="button" className="bg-transparent border-0 cursor-pointer text-xs font-bold text-[#B91C1C] underline underline-offset-2"
              onClick={connectGcal} data-testid="cal-gcal-reconnect">Google not synced — reconnect</button>
          ))}
          <label className="flex items-center gap-1.5 text-xs font-semibold text-[#000000] cursor-pointer ml-auto"
            title="Kill-switch for ALL client text messages (confirmations + reminders). Email reminders keep going out either way.">
            <input type="checkbox" data-testid="sms-kill-switch"
              checked={smsEnabled}
              onChange={async (e) => {
                try {
                  const { data } = await api.put("/admin/business-identity", { sms_enabled: e.target.checked });
                  setSmsEnabled(data.sms_enabled !== false);
                  toast.success(e.target.checked ? "Client texts ON" : "Client texts PAUSED — emails still go out");
                } catch (err) { toast.error(formatApiError(err)); }
              }} />
            Client texts (SMS)
          </label>
          <label className="flex items-center gap-1.5 text-xs font-semibold text-[#000000] cursor-pointer"
            title="Kill-switch for the evening 'your streak ends at midnight' emails to learners. One email max per person per day, 5-10 PM only.">
            <input type="checkbox" data-testid="streak-nudges-toggle"
              checked={streakNudges}
              onChange={async (e) => {
                try {
                  const { data } = await api.put("/admin/business-identity", { streak_nudges_enabled: e.target.checked });
                  setStreakNudges(data.streak_nudges_enabled !== false);
                  toast.success(e.target.checked ? "Streak nudges ON — evening save-your-streak emails" : "Streak nudges OFF");
                } catch (err) { toast.error(formatApiError(err)); }
              }} />
            Streak nudges
          </label>
          <label className="flex items-center gap-1.5 text-xs font-semibold text-[#000000] cursor-pointer"
            title="Show or hide the anonymous Community for members. While hidden, only admins can preview it.">
            <input type="checkbox" data-testid="community-toggle"
              checked={communityOn}
              onChange={async (e) => {
                try {
                  const { data } = await api.put("/admin/business-identity", { community_enabled: e.target.checked });
                  setCommunityOn(data.community_enabled === true);
                  toast.success(e.target.checked ? "Community is LIVE for members 🎉" : "Community hidden — only admins can see it");
                } catch (err) { toast.error(formatApiError(err)); }
              }} />
            Community
          </label>
        </div>
        {bookingView === "calendar" ? (
          <BookingsCalendar
            key={focus ? `${focus.id}-${focus.date}` : "default"}
            highlightId={focus?.id || highlightBooking}
            initialDate={focus?.date || deepLinkDate}
            initialView={focus ? "week" : undefined}
            autoOpenHighlight={!focus}
            refreshToken={calRefresh}
            onBlockRequest={(pre) => {
              setNewBlock({ date: pre.date, end_date: pre.end_date || "", start_time: pre.start_time || "", end_time: pre.end_time || "", label: "" });
              setBlockDlg(true);
            }}
            onChanged={() => api.get("/admin/site/bookings").then(({ data }) => setBookings(data))} />
        ) : (
          <>
        {bookings.length === 0 && <div className="text-sm text-[#000000]">No booking requests yet.</div>}
        {bookings.map((b) => (
          <div key={b.id}
            className={`flex flex-wrap items-center gap-2 border rounded-lg p-3 mb-2 ${b.id === highlightBooking ? "border-[#F59E0B] ring-2 ring-[#FDE68A]" : "border-[#EDEEF0]"}`}
            data-testid={`booking-row-${b.id}`}>
            <CalendarCheck size={16} className="text-[#000000]" />
            <div className="flex-1 min-w-[180px] cursor-pointer" title="View this week on the calendar"
              data-testid={`booking-row-jump-${b.id}`}
              onClick={() => { setFocus({ id: b.id, date: b.date }); setBookingView("calendar"); }}>
              <div className="text-sm font-bold text-[#000000]">
                {b.name} — {b.date} at {b.time}
                <span className={`ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${b.session_type === "virtual" ? "bg-[#DBEAFE] text-[#1D4ED8]" : "bg-[#ECFCCB] text-[#3F6212]"}`}>
                  {b.session_type === "virtual" ? "Virtual" : "In person"}
                </span>
                {b.client_confirmed && (
                  <span className="ml-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#FEF9C3] text-[#854D0E]" data-testid={`booking-client-confirmed-${b.id}`}>
                    ✓ Client confirmed
                  </span>
                )}
              </div>
              <div className="text-xs text-[#000000]">{b.phone}{b.email ? ` · ${b.email}` : ""}{b.client_tz ? ` · their tz: ${b.client_tz}` : ""}{b.note ? ` · "${b.note}"` : ""}
                {b.status === "cancelled" && b.cancelled_by === "client" && <span className="text-[#991B1B]"> · cancelled by client{b.cancel_reason ? `: "${b.cancel_reason}"` : ""}</span>}
                {b.reminder_24_sent ? <span className="text-[#3F6212]"> · reminded 48h+24h</span> : b.reminder_48_sent ? <span className="text-[#3F6212]"> · reminded 48h</span> : null}
              </div>
              {b.meet_link && <a href={b.meet_link} target="_blank" rel="noreferrer" className="text-xs text-[#1D4ED8] underline" onClick={(e) => e.stopPropagation()}>Google Meet link ↗</a>}
            </div>
            <span className={`text-xs font-bold uppercase ${b.status === "confirmed" ? "text-[#3F6212]" : b.status === "pending" ? "text-[#B45309]" : "text-[#991B1B]"}`}>{b.status}</span>
            {b.phone && (
              <button className="btn-secondary text-xs flex items-center gap-1" data-testid={`booking-text-${b.id}`}
                title={`Text ${b.name}`} onClick={() => setChat({ name: b.name, phone: b.phone, email: b.email, channel: "sms" })}>
                <ChatCircleText size={12} /> Text
              </button>
            )}
            {b.email && (
              <button className="btn-secondary text-xs flex items-center gap-1" data-testid={`booking-email-${b.id}`}
                title={`Email ${b.name}`} onClick={() => setChat({ name: b.name, phone: b.phone, email: b.email, channel: "email" })}>
                <EnvelopeSimple size={12} /> Email
              </button>
            )}
            {b.status === "pending" && (
              <>
                <button className="btn-primary text-xs" onClick={() => actBooking(b.id, "confirmed")} data-testid={`confirm-booking-${b.id}`}>Confirm</button>
                <button className="btn-secondary text-xs" onClick={() => actBooking(b.id, "declined")}>Decline</button>
              </>
            )}
            {b.status === "confirmed" && <button className="btn-secondary text-xs" onClick={() => actBooking(b.id, "cancelled")}>Cancel</button>}
          </div>
        ))}
          </>
        )}
        {chat && <ContactChatDialog contact={chat} defaultChannel={chat.channel} onClose={() => setChat(null)} />}
        {blockDlg && (
          <div className="fixed inset-0 z-[60] bg-black/50 grid place-items-center p-3" onClick={() => setBlockDlg(false)} data-testid="block-dialog-overlay">
            <div className="bg-white rounded-2xl p-4 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="block-dialog">
              <div className="font-display font-bold text-[#000000] mb-2">Block time off</div>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs font-semibold text-[#000000]">From
                  <input type="date" className="input-base mt-1 w-full text-sm" value={newBlock.date}
                    onChange={(e) => setNewBlock({ ...newBlock, date: e.target.value })} data-testid="block-from" />
                </label>
                <label className="text-xs font-semibold text-[#000000]">To — last day off, included (blank = one day)
                  <input type="date" className="input-base mt-1 w-full text-sm" value={newBlock.end_date}
                    onChange={(e) => setNewBlock({ ...newBlock, end_date: e.target.value })} data-testid="block-to" />
                </label>
                <label className="text-xs font-semibold text-[#000000]">Start (blank = all day)
                  <input type="time" className="input-base mt-1 w-full text-sm" value={newBlock.start_time}
                    onChange={(e) => setNewBlock({ ...newBlock, start_time: e.target.value })} data-testid="block-start-time" />
                </label>
                <label className="text-xs font-semibold text-[#000000]">End
                  <input type="time" className="input-base mt-1 w-full text-sm" value={newBlock.end_time}
                    onChange={(e) => setNewBlock({ ...newBlock, end_time: e.target.value })} data-testid="block-end-time" />
                </label>
              </div>
              <label className="text-xs font-semibold text-[#000000] block mt-2">Label (optional)
                <input className="input-base mt-1 w-full text-sm" placeholder="Vacation, tournament…" value={newBlock.label}
                  onChange={(e) => setNewBlock({ ...newBlock, label: e.target.value })} data-testid="block-label" />
              </label>
              <div className="flex justify-end gap-2 mt-3">
                <button className="btn-ghost text-xs" onClick={() => setBlockDlg(false)}>Cancel</button>
                <button className="btn-primary text-xs" data-testid="block-save"
                  onClick={async () => {
                    if (!newBlock.date) { toast.error("Pick a start date"); return; }
                    await addBlock();
                    setBlockDlg(false);
                    setCalRefresh((k) => k + 1);
                  }}>Block it</button>
              </div>
            </div>
          </div>
        )}
        <div className="mt-4 border-t border-[#EDEEF0] pt-3" data-testid="calendar-subsections">
          <SubSection title="Booking Hours & Settings" testid="cal-sub-settings">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3">
              <label className="block text-xs font-semibold text-[#000000] mb-3">Session length (minutes)
                <input type="number" className="input-base mt-1 w-full text-sm" value={booking.slot_minutes} onChange={(e) => setBooking({ ...booking, slot_minutes: parseInt(e.target.value) || 60 })} data-testid="slot-minutes" />
              </label>
              <label className="block text-xs font-semibold text-[#000000] mb-3" title="How far into the future clients can book online. Days beyond this show as unavailable — this is why far-out months look grey.">Book up to … days ahead
                <input type="number" className="input-base mt-1 w-full text-sm" value={booking.max_days_out} onChange={(e) => setBooking({ ...booking, max_days_out: parseInt(e.target.value) || 60 })} data-testid="max-days-out" />
              </label>
              <label className="block text-xs font-semibold text-[#000000] mb-3" title="Every day inside this window shows as fully booked — stops last-minute bookings">Show "fully booked" for next … days
                <input type="number" className="input-base mt-1 w-full text-sm" value={booking.full_days_ahead} onChange={(e) => setBooking({ ...booking, full_days_ahead: parseInt(e.target.value) || 0 })} data-testid="full-days-ahead" />
              </label>
              <label className="block text-xs font-semibold text-[#000000] mb-3" title="Beyond the fully-booked window, this % of open slots shows as taken so you look busy">% of open slots shown as busy
                <input type="number" className="input-base mt-1 w-full text-sm" value={booking.fake_busy_pct} onChange={(e) => setBooking({ ...booking, fake_busy_pct: parseInt(e.target.value) || 0 })} data-testid="fake-busy-pct" />
              </label>
            </div>
            <label className="block text-xs font-semibold text-[#000000] mb-3" title="New booking requests are texted to this number with a link straight to that day on the calendar. Leave blank to get emails instead.">Text new booking requests to (your cell)
              <input type="tel" className="input-base mt-1 w-full max-w-xs text-sm" placeholder="e.g. 403-555-0123 — blank = email instead" value={booking.alert_phone || ""} onChange={(e) => setBooking({ ...booking, alert_phone: e.target.value })} data-testid="booking-alert-phone" />
              {(() => {
                const d = (booking.alert_phone || "").replace(/\D/g, "");
                if (!d) return null;
                const ok = d.length === 10 || (d.length === 11 && d.startsWith("1"));
                const ten = d.slice(-10);
                return ok
                  ? <span className="block mt-1 font-normal text-[11px] text-[#3F6212]" data-testid="alert-phone-ok">✓ Texts will go to +1 ({ten.slice(0, 3)}) {ten.slice(3, 6)}-{ten.slice(6)}</span>
                  : <span className="block mt-1 font-normal text-[11px] text-[#B45309]" data-testid="alert-phone-warn">Needs a 10-digit Canadian/US number (dashes and spaces are fine)</span>;
              })()}
            </label>
            <div className="max-w-md">
              <ImageField
                label="Booking page image — shown when clients land on the confirm / cancel pages from texts & emails (replaces the plain checkmark)"
                value={booking.page_image_url || ""}
                onChange={(v) => setBooking({ ...booking, page_image_url: v })}
                testid="booking-page-image"
              />
            </div>

            <div className="mt-4 rounded-xl border border-[#C4C8CE] p-3" data-testid="session-info-settings">
              <div className="text-sm font-display font-bold">Shareable session page</div>
              <div className="text-xs text-[#52525B] mb-2">Every booking gets a public page at /session/… with time, place, and what to wear — the link is in every confirmation and reminder email, and on each booking in the calendar below. Perfect for workshop contacts to forward to their group.</div>
              <label className="block text-xs font-semibold text-[#000000] mb-2">Location shown on the page (blank = your booking address)
                <input className="input-base mt-1 w-full text-sm" value={booking.session_location || ""} onChange={(e) => setBooking({ ...booking, session_location: e.target.value })} data-testid="session-location" />
              </label>
              <label className="block text-xs font-semibold text-[#000000] mb-2">What to wear
                <input className="input-base mt-1 w-full text-sm" value={booking.session_what_to_wear || ""} onChange={(e) => setBooking({ ...booking, session_what_to_wear: e.target.value })} data-testid="session-wear" />
              </label>
              <label className="block text-xs font-semibold text-[#000000]">What to bring
                <input className="input-base mt-1 w-full text-sm" value={booking.session_bring || ""} onChange={(e) => setBooking({ ...booking, session_bring: e.target.value })} data-testid="session-bring-input" />
              </label>
              <div className="max-w-md mt-2">
                <ImageField
                  label="Page photo / logo — shown at the top of the session page (blank = uses your booking page image)"
                  value={booking.session_image_url || ""}
                  onChange={(v) => setBooking({ ...booking, session_image_url: v })}
                  testid="session-image-field"
                />
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-[#C4C8CE] p-3" data-testid="reminder-settings">
              <div className="text-sm font-display font-bold">Reminder schedule</div>
              <div className="text-xs text-[#52525B] mb-2">Automatic reminders for confirmed sessions. Untick a stage to skip it; the optional note is added to that reminder's email.</div>
              {[["7d", "1 week before (email only)"], ["48h", "48 hours before (email + text)"], ["24h", "24 hours before (email + text)"], ["2h", "2 hours before (email + text)"]].map(([k, label]) => {
                const r = (booking.reminders || {})[k] || { enabled: true, custom_text: "" };
                const setR = (patch) => setBooking({ ...booking, reminders: { ...(booking.reminders || {}), [k]: { ...r, ...patch } } });
                return (
                  <div key={k} className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 py-1.5 border-t border-[#ECEDEF] first:border-0">
                    <label className="flex items-center gap-2 text-xs font-semibold text-[#000000] w-56 shrink-0 cursor-pointer">
                      <input type="checkbox" checked={r.enabled !== false} onChange={(e) => setR({ enabled: e.target.checked })} data-testid={`reminder-toggle-${k}`} />
                      {label}
                    </label>
                    <input className="input-base w-full text-xs" placeholder="Optional note added to this reminder email…" maxLength={300}
                      value={r.custom_text || ""} onChange={(e) => setR({ custom_text: e.target.value })} data-testid={`reminder-note-${k}`} disabled={r.enabled === false} />
                  </div>
                );
              })}
            </div>
            <div className="text-xs font-bold text-[#000000] uppercase tracking-wide mb-2">Weekly hours</div>
            {WEEKDAYS.map((name, day) => {
              const ranges = (booking.hours || {})[String(day)] || [];
              const on = ranges.length > 0;
              return (
                <div key={day} className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
                  <label className="flex items-center gap-2 w-24 sm:w-28 shrink-0 text-sm font-semibold cursor-pointer">
                    <input type="checkbox" checked={on} onChange={(e) => toggleDay(day, e.target.checked)} data-testid={`day-toggle-${day}`} /> {name}
                  </label>
                  {on ? (
                    <div className="flex items-center gap-1.5 text-sm min-w-0">
                      <input type="time" className="input-base text-sm" style={{ padding: "8px 6px", width: 128 }} value={ranges[0][0]} onChange={(e) => setHours(day, 0, 0, e.target.value)} />
                      <span className="text-[#000000]">to</span>
                      <input type="time" className="input-base text-sm" style={{ padding: "8px 6px", width: 128 }} value={ranges[0][1]} onChange={(e) => setHours(day, 0, 1, e.target.value)} />
                    </div>
                  ) : <span className="text-xs text-[#000000]">Closed</span>}
                </div>
              );
            })}
            <button className="btn-primary text-sm mt-2" onClick={saveBooking} data-testid="save-booking-settings">Save booking settings</button>
          </SubSection>

          <SubSection title="Time Off / Blocked Times" testid="cal-sub-blocks">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end mb-3">
              <label className="text-xs font-semibold text-[#000000]">From date
                <input type="date" className="input-base mt-1 w-full text-sm" value={newBlock.date} onChange={(e) => setNewBlock({ ...newBlock, date: e.target.value })} data-testid="block-date" />
              </label>
              <label className="text-xs font-semibold text-[#000000]">To date — your last day off, included
                <input type="date" className="input-base mt-1 w-full text-sm" value={newBlock.end_date} onChange={(e) => setNewBlock({ ...newBlock, end_date: e.target.value })} />
              </label>
              <label className="text-xs font-semibold text-[#000000]">Start (blank = all day)
                <input type="time" className="input-base mt-1 w-full text-sm" value={newBlock.start_time} onChange={(e) => setNewBlock({ ...newBlock, start_time: e.target.value })} />
              </label>
              <label className="text-xs font-semibold text-[#000000]">End
                <input type="time" className="input-base mt-1 w-full text-sm" value={newBlock.end_time} onChange={(e) => setNewBlock({ ...newBlock, end_time: e.target.value })} />
              </label>
              <label className="text-xs font-semibold text-[#000000]">Label
                <input className="input-base mt-1 w-full text-sm" placeholder="e.g. Vacation" value={newBlock.label} onChange={(e) => setNewBlock({ ...newBlock, label: e.target.value })} data-testid="block-list-label" />
              </label>
            </div>
            <button className="btn-secondary text-sm" onClick={async () => { await addBlock(); setCalRefresh((k) => k + 1); }} data-testid="add-block-button">Block this time</button>
            <div className="mt-3">
              {blocks.length === 0 && <div className="text-xs text-[#000000]">No time off blocked. Tip: you can also drag across the calendar above to block time.</div>}
              {blocks.map((b) => (
                <div key={b.id} className="flex items-center gap-2 text-sm border border-[#EDEEF0] rounded-lg px-3 py-2 mb-1.5">
                  <span className="flex-1">{b.date}{b.end_date && b.end_date !== b.date ? ` → ${b.end_date}` : ""}{b.start_time ? ` · ${b.start_time}–${b.end_time || "end"}` : " · all day"}{b.label ? ` — ${b.label}` : ""}</span>
                  <button className="btn-ghost p-1 cursor-pointer" onClick={async () => { await delBlock(b.id); setCalRefresh((k) => k + 1); }} data-testid={`delete-block-${b.id}`}><Trash size={14} /></button>
                </div>
              ))}
            </div>
          </SubSection>

          <SubSection title="Google Calendar Sync" testid="cal-sub-gcal">
            {gcal === null ? <div className="text-sm text-[#000000]">Loading…</div> : !gcal.configured ? (
              <div className="text-sm text-[#000000]">
                Not set up yet. Once your Google credentials are added, one click here links your <b>elevation650@gmail.com</b> calendar:
                confirmed bookings appear on it automatically, and anything on it (doctor appts, family plans) blocks those times from being booked.
              </div>
            ) : gcal.connected ? (
              <div className="flex items-center gap-3 text-sm flex-wrap">
                <GoogleLogo size={18} /> Connected{gcal.email ? <> as <b>{gcal.email}</b></> : " to your Google Calendar"}
                <button className="btn-primary text-xs" onClick={syncGcalNow} data-testid="gcal-sync-now">Sync now</button>
                <button className="btn-secondary text-xs" onClick={async () => { await api.delete("/admin/site/gcal"); load(); }}>Disconnect</button>
                <div className="w-full text-xs text-[#000000]">Auto-sync runs every 6 hours and after every confirmation — any booking that missed the calendar gets pushed. If Google ever drops the connection you'll get a text with a one-tap reconnect link.</div>
              </div>
            ) : gcal.needs_reconnect ? (
              <div data-testid="gcal-reconnect">
                <div className="text-sm text-[#B91C1C] font-semibold mb-2">
                  Google revoked the connection{gcal.email ? <> for <b>{gcal.email}</b></> : ""} — bookings are NOT syncing. Reconnect below.
                </div>
                <div className="text-xs text-[#000000] mb-2">
                  Tip: if your Google Cloud OAuth consent screen is in "Testing" mode, Google expires the connection every 7 days — set it to "In production" (Google Cloud Console → APIs & Services → OAuth consent screen → Publish app) so this stops happening.
                </div>
                <button className="btn-primary text-sm flex items-center gap-2" onClick={connectGcal} data-testid="gcal-reconnect-button">
                  <GoogleLogo size={16} /> Reconnect Google Calendar
                </button>
              </div>
            ) : (
              <button className="btn-primary text-sm flex items-center gap-2" onClick={connectGcal} data-testid="gcal-connect-button">
                <GoogleLogo size={16} /> Connect Google Calendar
              </button>
            )}
          </SubSection>
        </div>
      </Card>

      <Card title="Private Sessions — In Person & Virtual">
        <Toggle label="Offer virtual sessions (Google Meet)" checked={c.booking?.virtual_enabled !== false} onChange={(v) => set("booking", "virtual_enabled", v)} testid="booking-virtual-toggle" />
        <div className="grid md:grid-cols-2 gap-4">
          <div className="border border-[#EDEEF0] rounded-lg p-3">
            <div className="text-xs font-bold uppercase text-[#3F6212] mb-2">In person</div>
            <Field label="Button title" value={c.booking?.inperson_title} onChange={(v) => set("booking", "inperson_title", v)} />
            <Field label="Short description" textarea value={c.booking?.inperson_blurb} onChange={(v) => set("booking", "inperson_blurb", v)} testid="booking-inperson-blurb" />
            <Field label="Training address (used for the Google Maps pin)" value={c.booking?.address} onChange={(v) => set("booking", "address", v)} testid="booking-address" />
            <Field label="Custom Google Maps link (optional — overrides address pin)" value={c.booking?.maps_url} onChange={(v) => set("booking", "maps_url", v)} placeholder="https://maps.app.goo.gl/…" />
          </div>
          <div className="border border-[#EDEEF0] rounded-lg p-3">
            <div className="text-xs font-bold uppercase text-[#1D4ED8] mb-2">Virtual (Google Meet)</div>
            <Field label="Button title" value={c.booking?.virtual_title} onChange={(v) => set("booking", "virtual_title", v)} />
            <Field label="Short description" textarea value={c.booking?.virtual_blurb} onChange={(v) => set("booking", "virtual_blurb", v)} testid="booking-virtual-blurb" />
            <div className="text-xs text-[#000000]">The Google Meet link is created automatically on your calendar when you confirm a virtual booking, and is included in the confirmation email. Times are converted to the client's timezone for them — your calendar always shows your local time.</div>
          </div>
        </div>
        <div className="mt-4 border-t border-[#EDEEF0] pt-3">
          <div className="text-sm font-bold text-[#000000] mb-1">Confirmation email & text</div>
          <div className="text-xs text-[#000000] mb-3">Sent when you confirm a booking (email if the client left one, text if they left a phone number). You can use these placeholders: <code>{"{name} {date} {time} {time_local} {maps_link} {meet_link} {address}"}</code></div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Field label="In-person email subject" value={c.booking?.email_inperson_subject} onChange={(v) => set("booking", "email_inperson_subject", v)} testid="email-inperson-subject" />
              <Field label="In-person email body" textarea value={c.booking?.email_inperson_body} onChange={(v) => set("booking", "email_inperson_body", v)} testid="email-inperson-body" />
              <Field label={'In-person text (SMS) — placeholders: {name} {date} {time}'} textarea value={c.booking?.sms_inperson_template} onChange={(v) => set("booking", "sms_inperson_template", v)} testid="sms-inperson-template" />
              <PdfField label={'"What to expect" PDF — attached to BOTH the in-person email and text (MMS)'} value={c.booking?.expect_inperson_pdf} onChange={(v) => set("booking", "expect_inperson_pdf", v)} testid="pdf-inperson" />
            </div>
            <div>
              <Field label="Virtual email subject" value={c.booking?.email_virtual_subject} onChange={(v) => set("booking", "email_virtual_subject", v)} testid="email-virtual-subject" />
              <Field label="Virtual email body" textarea value={c.booking?.email_virtual_body} onChange={(v) => set("booking", "email_virtual_body", v)} testid="email-virtual-body" />
              <Field label={'Virtual text (SMS) — placeholders: {name} {date} {time} {meet_link}'} textarea value={c.booking?.sms_virtual_template} onChange={(v) => set("booking", "sms_virtual_template", v)} testid="sms-virtual-template" />
              <PdfField label={'"What to expect" PDF — attached to BOTH the virtual email and text (MMS)'} value={c.booking?.expect_virtual_pdf} onChange={(v) => set("booking", "expect_virtual_pdf", v)} testid="pdf-virtual" />
            </div>
          </div>
          <div className="text-xs text-[#000000] mt-1">Only one PDF uploaded? No problem — if a slot is empty the other PDF is used, so all four messages (both emails and both texts) carry the same attachment. Saved with "Save website".</div>
        </div>
      </Card>
      </>}

      {isCal ? <>
      <SessionPackagesCard />
      <CreditAccountsCard />
      </> : <>
      <EventsCard />
      <BlogCard />
      <QrCodesCard />
      <CourseBroadcastCard />

      <CommsLogCard />

      <LoginAlertsCard />
      </>}

      {!isCal && <>
      <Card title="Privacy Policy & Terms">
        <p className="text-xs text-[#000000] mb-3">
          Live at <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#3F6212] font-bold">/privacy</a> and{" "}
          <a href="/terms" target="_blank" rel="noreferrer" className="text-[#3F6212] font-bold">/terms</a> (linked in your website footer, and used by Google's OAuth consent screen).
          The boxes below are pre-filled with the exact text currently shown on those pages — edit freely.
          Formatting: start a line with <code>## </code> for a heading, <code>- </code> for a bullet. Saved with "Save website".
        </p>
        <div className="grid lg:grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-[#000000]">Privacy Policy
            <textarea className="input-base mt-1 w-full text-xs font-mono" rows={14}
              placeholder="Leave empty to use the built-in policy…"
              value={c.legal?.privacy || ""} onChange={(e) => set("legal", "privacy", e.target.value)}
              data-testid="legal-privacy-input" />
          </label>
          <label className="text-xs font-semibold text-[#000000]">Terms of Service
            <textarea className="input-base mt-1 w-full text-xs font-mono" rows={14}
              placeholder="Leave empty to use the built-in terms…"
              value={c.legal?.terms || ""} onChange={(e) => set("legal", "terms", e.target.value)}
              data-testid="legal-terms-input" />
          </label>
        </div>
        <Field label={'"Last updated" label shown on both pages'} value={c.legal?.updated || ""} onChange={(v) => set("legal", "updated", v)} placeholder="June 2026" />
      </Card>

      <Card title="Website Messages">
        {messages.length === 0 && <div className="text-sm text-[#000000]">No messages yet — they also arrive by email.</div>}
        {messages.map((m) => (
          <div key={m.id} className="border border-[#EDEEF0] rounded-lg p-3 mb-2">
            <div className="flex items-center gap-2 text-xs text-[#000000]">
              <Envelope size={14} /> <b className="text-[#000000]">{m.name}</b> {m.email && `· ${m.email}`} {m.phone && `· ${m.phone}`} · {new Date(m.at).toLocaleString()}
            </div>
            <div className="text-sm text-[#000000] mt-1 whitespace-pre-wrap">{m.message}</div>
          </div>
        ))}
      </Card>

      <Card title="Coming Soon Modules">
        <div className="text-sm text-[#000000]">
          <p className="mb-2">Built to be switched on when you're ready — no rebuild needed:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Blog</b> — SEO-friendly articles you write in the same easy editor style.</li>
            <li><b>Community</b> — an anonymous member discussion space (like a private Facebook group).</li>
          </ul>
        </div>
      </Card>
      </>}
    </div>
  );
}
